import React, { useContext, useEffect, useRef } from 'react';
import { Grid, TextField, InputAdornment, IconButton } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { PrimaryButton } from '@/styles/blocks';
import { colors } from '@/styles';
import { css } from '@emotion/react';
import { isFinite } from 'lodash';
import { Context } from './store';
import { Body1, H2 } from './blocks';

const dollarFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
});

const ChargeRow = ({ charge, editable, callbacks }) => {
    const chargeKey = charge.supplemental_charge_id || charge._tempId;
    const total = (charge.rate || 0) * (charge.quantity || 0);

    return (
        <Grid
            container
            direction="row"
            css={css`
                border-bottom: 1px solid lightgrey;
                padding: 8px 5px;
                flex-wrap: nowrap;
                align-items: center;
            `}
        >
            {editable && (
                <Grid
                    container
                    direction="column"
                    css={css`
                        flex: 0 0 36px;
                        max-width: 36px;
                        margin-right: 8px;
                    `}
                >
                    <IconButton size="small" onClick={() => callbacks.removeSupplementalCharge(chargeKey)}>
                        <DeleteForeverIcon
                            css={css`
                                color: ${colors.reds.cancelled};
                            `}
                        />
                    </IconButton>
                </Grid>
            )}
            <Grid
                container
                direction="column"
                css={css`
                    flex: 1;
                    margin-right: 8px;
                    max-width: 120px;
                `}
            >
                <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="Code"
                    disabled={!editable}
                    value={charge.code || ''}
                    onChange={(e) => callbacks.editSupplementalCharge(chargeKey, { code: e.target.value })}
                />
            </Grid>
            <Grid
                container
                direction="column"
                css={css`
                    flex: 2;
                    margin-right: 8px;
                `}
            >
                <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="Description"
                    disabled={!editable}
                    value={charge.description || ''}
                    onChange={(e) => callbacks.editSupplementalCharge(chargeKey, { description: e.target.value })}
                />
            </Grid>
            <Grid
                container
                direction="column"
                css={css`
                    flex: 1;
                    margin-right: 8px;
                    max-width: 120px;
                `}
            >
                <TextField
                    fullWidth
                    variant="outlined"
                    type="number"
                    size="small"
                    disabled={!editable}
                    value={charge.rate}
                    onChange={(e) => {
                        const next = parseFloat(e.target.value);
                        callbacks.editSupplementalCharge(chargeKey, { rate: isNaN(next) ? '' : next });
                    }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        inputProps: { style: { textAlign: 'center' } },
                    }}
                />
            </Grid>
            <Grid
                container
                direction="column"
                css={css`
                    flex: 1;
                    margin-right: 8px;
                    max-width: 80px;
                `}
            >
                <TextField
                    fullWidth
                    variant="outlined"
                    type="number"
                    size="small"
                    disabled={!editable}
                    value={charge.quantity}
                    onChange={(e) => {
                        const next = parseFloat(e.target.value);
                        callbacks.editSupplementalCharge(chargeKey, { quantity: isNaN(next) ? '' : next });
                    }}
                    InputProps={{
                        inputProps: { style: { textAlign: 'center' } },
                    }}
                />
            </Grid>
            <Grid
                container
                direction="column"
                css={css`
                    flex: 1;
                    margin-right: 8px;
                    max-width: 160px;
                `}
            >
                <Autocomplete
                    freeSolo
                    size="small"
                    disabled={!editable}
                    options={[]}
                    value={charge.qbo_tag || ''}
                    onInputChange={(_, value) => {
                        callbacks.editSupplementalCharge(chargeKey, { qbo_tag: value || '' });
                    }}
                    renderInput={(params) => (
                        <TextField {...params} size="small" variant="outlined" placeholder="QBO Tag" />
                    )}
                />
            </Grid>
            <Grid
                container
                direction="column"
                css={css`
                    flex: 0 0 100px;
                    max-width: 100px;
                    justify-content: center;
                    align-items: flex-end;
                `}
            >
                <Body1
                    css={css`
                        color: ${total < 0 ? colors.reds.primary : 'inherit'};
                    `}
                >
                    {isFinite(total) ? dollarFormatter.format(total) : '--'}
                </Body1>
            </Grid>
        </Grid>
    );
};

const SupplementalCharges = () => {
    const { state, loading, callbacks } = useContext(Context);

    // Scroll the supplemental charges section into view when a charge is added.
    const sectionRef = useRef(null);
    const prevTempIdsRef = useRef(null);

    useEffect(() => {
        const currentTempIds = (state.supplementalCharges || []).map((c) => c._tempId).filter(Boolean);
        const prev = prevTempIdsRef.current;
        prevTempIdsRef.current = currentTempIds;

        // Skip the first run so pre-existing rows don't trigger a scroll on mount.
        if (prev === null) return;

        const added = currentTempIds.some((id) => !prev.includes(id));
        if (added) {
            sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [state.supplementalCharges]);

    if (!state.supplementalCharges?.length && !state.hasSupplementalChanges) return null;

    return (
        <Grid
            ref={sectionRef}
            container
            direction="column"
            css={css`
                padding: 0 40px;
                margin-bottom: 20px;
            `}
        >
            <Grid
                container
                direction="column"
                css={css`
                    background: ${colors.white.primary};
                    box-shadow: 0px 1px 3px 0px #3f3f4426;
                    box-shadow: 0px 0px 0px 1px #3f3f440d;
                    padding: 18px;
                `}
            >
                <Grid
                    container
                    direction="row"
                    css={css`
                        align-items: center;
                        margin-bottom: 12px;
                    `}
                >
                    <Grid
                        container
                        direction="column"
                        css={css`
                            flex: 1;
                        `}
                    >
                        <H2>Supplemental Charges ({state.supplementalCharges?.length || 0})</H2>
                    </Grid>
                    {state.hasSupplementalChanges && state.canEditSupplemental && (
                        <Grid
                            container
                            direction="row"
                            css={css`
                                flex: 0;
                                flex-basis: content;
                                gap: 8px;
                            `}
                        >
                            <PrimaryButton
                                css={css`
                                    height: 36px;
                                    justify-content: center;
                                    background-color: ${colors.white.primary};
                                    color: #4c4c4c;
                                    border: 1px solid #4c4c4c;
                                    :hover {
                                        background-color: #f5f5f5 !important;
                                    }
                                `}
                                onClick={callbacks.discardSupplementalChanges}
                            >
                                Cancel Changes
                            </PrimaryButton>
                            <PrimaryButton
                                css={css`
                                    height: 36px;
                                    justify-content: center;
                                `}
                                disabled={loading.supplemental}
                                onClick={callbacks.saveSupplementalCharges}
                            >
                                Save
                            </PrimaryButton>
                        </Grid>
                    )}
                </Grid>

                {state.supplementalCharges?.length > 0 && (
                    <>
                        <Grid
                            container
                            direction="row"
                            css={css`
                                border-bottom: 2px solid #4c4c4c;
                                padding: 4px 5px;
                                flex-wrap: nowrap;
                                align-items: center;
                            `}
                        >
                            {state.canEditSupplemental && (
                                <Grid
                                    container
                                    direction="column"
                                    css={css`
                                        flex: 0 0 36px;
                                        max-width: 36px;
                                        margin-right: 8px;
                                    `}
                                />
                            )}
                            <Grid
                                container
                                direction="column"
                                css={css`
                                    flex: 1;
                                    margin-right: 8px;
                                    max-width: 120px;
                                `}
                            >
                                <Body1>Code</Body1>
                            </Grid>
                            <Grid
                                container
                                direction="column"
                                css={css`
                                    flex: 2;
                                    margin-right: 8px;
                                `}
                            >
                                <Body1>Description</Body1>
                            </Grid>
                            <Grid
                                container
                                direction="column"
                                css={css`
                                    flex: 1;
                                    margin-right: 8px;
                                    max-width: 120px;
                                `}
                            >
                                <Body1>Rate</Body1>
                            </Grid>
                            <Grid
                                container
                                direction="column"
                                css={css`
                                    flex: 1;
                                    margin-right: 8px;
                                    max-width: 80px;
                                `}
                            >
                                <Body1>Qty</Body1>
                            </Grid>
                            <Grid
                                container
                                direction="column"
                                css={css`
                                    flex: 1;
                                    margin-right: 8px;
                                    max-width: 160px;
                                `}
                            >
                                <Body1>QBO Tag</Body1>
                            </Grid>
                            <Grid
                                container
                                direction="column"
                                css={css`
                                    flex: 0 0 100px;
                                    max-width: 100px;
                                    align-items: flex-end;
                                `}
                            >
                                <Body1>Total</Body1>
                            </Grid>
                        </Grid>

                        {state.supplementalCharges.map((charge) => (
                            <ChargeRow
                                key={charge.supplemental_charge_id || charge._tempId}
                                charge={charge}
                                editable={state.canEditSupplemental}
                                callbacks={callbacks}
                            />
                        ))}
                    </>
                )}
            </Grid>
        </Grid>
    );
};

export default SupplementalCharges;
